import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import { Box, Button, Divider, OutlinedInput, Paper, TextareaAutosize } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { TEXT_DEFAULT_MAX_LENGTH } from "config/constants";
import { NO_TEXT_LABEL } from "config/LabelMapping";
import { useBoundingBoxesContext } from "context/boundingBoxesContext";
import { useReviewPageHotkeys } from "hooks/useHotkeys/useHotkeys";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { ANSWER_VALUES, Posicheck } from "types/imageQueries";

import { UnclearActionsRow } from "./UnclearActionsRow";

export interface TextRecognitionImageActionsProps {
  imageQueryId?: Posicheck["id"];
  isUnclearMenuOpen: boolean;
  setUnclearMenuOpen: (isOpen: boolean) => void;
  onUnclearSubmit: (feedbackText: string) => void;
  onLabelSubmit: (value: string) => void;
  defaultLabel?: string;
  valueMaxLength: number | undefined;
  showDeleteButton?: boolean;
  onDeleteImageQuery?: () => void | Promise<void>;
  isDeletingImageQuery?: boolean;
}

export function TextRecognitionImageActions(props: TextRecognitionImageActionsProps) {
  const {
    imageQueryId,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    onUnclearSubmit,
    onLabelSubmit,
    defaultLabel,
    valueMaxLength = TEXT_DEFAULT_MAX_LENGTH,
    showDeleteButton,
    onDeleteImageQuery,
    isDeletingImageQuery,
  } = props;

  const { textRecognitionValue: value, setTextRecognitionValue: setValue } = useBoundingBoxesContext();
  const [valueError, setValueError] = useState<string>("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const hasImageQuery = Boolean(imageQueryId);

  // Initialize with defaultLabel only if value is empty
  useEffect(() => {
    if (defaultLabel && defaultLabel !== ANSWER_VALUES.UNCLEAR && value === "") {
      setValue(defaultLabel);
    }
  }, [defaultLabel, setValue, value]);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.focus();
    // Move cursor to the end of the text
    const { length } = value;
    ref.current.setSelectionRange(length, length);
  }, [imageQueryId, value]);

  const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;

    if (value !== NO_TEXT_LABEL && !value) {
      setValueError("Please set a value");
      return;
    }
    setValueError("");
    setValue(value);
  };

  const handleSubmit = () => {
    const inputValue = ref.current?.value;

    if (inputValue !== NO_TEXT_LABEL && !inputValue) {
      setValueError("The text string value is required");
      return;
    }

    if (valueError) return;

    onLabelSubmit(inputValue);
  };

  const handleNotPresentSubmit = () => {
    onLabelSubmit(NO_TEXT_LABEL);
  };

  const isSubmitEnabled = value.trim().length > 0 && value.trim().length <= valueMaxLength;

  const { useTextRecognitionHotkeys } = useReviewPageHotkeys({
    detectorMode: DetectorModes.TEXT_RECOGNITION,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    hasImageQuery,
    onLabelSubmit,
    inputValue: value,
    valueError,
    setValueError,
  });

  useTextRecognitionHotkeys();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      if (!event.ctrlKey && !event.shiftKey && isSubmitEnabled) {
        // Do not add new line when the answer is submitted when pressing Enter
        event.preventDefault();
        // This is to avoid submitting the form when pressing Enter with ctrl or shift to add a new line
        handleSubmit();
        return;
      }
      setValue((prevValue) => `${prevValue}\n`);
    }
  };

  return (
    <Box id="text-recognition-actions" display="flex" className="bg-[#E5E9ED] rounded m-[22px] h-[136px] items-center">
      <Box id="text-recognition-actions-options" flexGrow={1}>
        <Paper
          elevation={0}
          className="p-[20px] pt-[10px] rounded-lg"
          sx={{ textAlign: "center", width: "100%", position: "relative" }}
        >
          <Typography id="text-recognition-actions-options-title" color="#006DD9" className="mb-[5px]">
            Text from image:
          </Typography>
          <OutlinedInput
            id="text-recognition-actions-options-input"
            value={value}
            inputComponent={TextareaAutosize}
            inputProps={{
              ref,
              name: "imageText",
              onChange: onInputChange,
              placeholder: value === "" ? "" : "Enter text string here",
              style: { resize: "none" },
              minRows: 3,
              maxRows: 3,
              maxLength: valueMaxLength,
              onKeyDown: handleKeyDown,
            }}
            fullWidth
            sx={{ flex: 1, "& .MuiInputBase-input": { paddingX: "10px", paddingY: "5px" } }}
          />
          <Typography
            id="text-recognition-actions-options-input-length"
            variant="small"
            color={value.length >= valueMaxLength ? "error.main" : "text.disabled"}
            sx={{
              position: "absolute",
              bottom: 6,
              right: 20,
            }}
          >
            {value.length || 0} / {valueMaxLength}
          </Typography>
        </Paper>
      </Box>
      <Divider
        id="text-recognition-actions-divider"
        orientation="vertical"
        flexItem
        sx={{
          marginX: "20px",
          height: "136px",
          borderColor: "#000000",
        }}
      />
      <Box id="text-recognition-actions-unclear" flexShrink={0}>
        <Box display="flex" gap="5px" marginBottom="10px">
          <UnclearActionsRow
            hasImageQuery={hasImageQuery}
            isUnclearMenuOpen={isUnclearMenuOpen}
            setUnclearMenuOpen={setUnclearMenuOpen}
            onUnclearSubmit={onUnclearSubmit}
            hideHotkey
            showDeleteButton={showDeleteButton}
            onDeleteImageQuery={onDeleteImageQuery}
            isDeletingImageQuery={isDeletingImageQuery}
          />
        </Box>
        <Button
          id="text-recognition-actions-not-present-button"
          fullWidth
          onClick={handleNotPresentSubmit}
          variant="outlined"
          sx={{
            height: "34px",
            bgcolor: "#FFFFFF",
            borderRadius: "17px",
            textTransform: "none",
            justifyContent: "center",
            color: "#0D297D",
            fontSize: "16px",
            padding: "0px 18px",
            border: "1px solid #0D297D",
            marginBottom: "10px",
          }}
        >
          Text not present
        </Button>
        <Button
          id="text-recognition-actions-submit-button"
          fullWidth
          onClick={handleSubmit}
          variant="contained"
          sx={{
            height: "48px",
            bgcolor: "#006DD9",
            borderRadius: "22px",
            textTransform: "none",
            justifyContent: "center",
            fontSize: "20px",
          }}
          disabled={!isSubmitEnabled}
          endIcon={
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                height: "100%",
                width: "60px",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#FFFFFF",
                opacity: 0.6,
                gap: "5px",
              }}
            >
              <KeyboardOutlinedIcon sx={{ height: "24px", width: "24px" }} />
              <Typography variant="small" fontWeight="bold">
                Enter
              </Typography>
            </Box>
          }
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
}
